import { useEffect, useCallback } from 'react';
import { useRecoilValue, useRecoilState } from 'recoil';
import { useCookies } from 'react-cookie';

import { useUserIsLoggedIn } from '@app/user';
import { useDeviceId } from '@app/experiments';

import { messageIsReadUpdatesState, inboxNewMessageExistState } from '../state';
import useInboxMessages from './useInboxMessages';
import useOffersMessageList from './useOffersMessageList';

const lastSeenInboxMsgCookie = 'stockx_message_last_seen_inbox';
const lastSeenOfferMsgCookie = 'stockx_message_last_seen_offer';

// Hook that return a flag whether they are new messages to enable the dot on the notification icon
const useShowDot = ({ enabled = true } = {}) => {
  const loggedIn = useUserIsLoggedIn();
  const deviceId = useDeviceId();

  const {
    messages: inboxMessages,
    loading: inboxMsgsLoading,
    error: inboxMsgsError,
  } = useInboxMessages(deviceId, 35, loggedIn);

  const {
    loading: offerMsgsLoading,
    error: offerMsgsError,
    offerMessages,
  } = useOffersMessageList({ inDropdown: true, enabled });

  const messageIsReadUpdates = useRecoilValue(messageIsReadUpdatesState);
  const [cookies, setCookie] = useCookies([
    lastSeenInboxMsgCookie,
    lastSeenOfferMsgCookie,
  ]);

  const [showDot, setShowDot] = useRecoilState(inboxNewMessageExistState);

  const clearDot = useCallback(() => setShowDot(false), [setShowDot]);

  // Need handling for login again with a different user
  useEffect(() => {
    if (!inboxMsgsLoading && !inboxMsgsError && inboxMessages?.length > 0) {
      const recentMsgTime =
        (!inboxMessages[0]?.node?.isRead &&
          !messageIsReadUpdates.includes(inboxMessages[0]?.node?.id) &&
          inboxMessages[0]?.node?.deliveredAt) ||
        '';

      if (!recentMsgTime) {
        return;
      }

      if (!cookies[lastSeenInboxMsgCookie]) {
        setCookie(lastSeenInboxMsgCookie, recentMsgTime);
        setShowDot(true);
      }

      if (new Date(cookies[lastSeenInboxMsgCookie]) < new Date(recentMsgTime)) {
        setCookie(lastSeenInboxMsgCookie, recentMsgTime);
        setShowDot(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxMessages, inboxMsgsLoading]);

  useEffect(() => {
    if (
      !offerMsgsLoading &&
      !offerMsgsError &&
      offerMessages?.length > 0 &&
      offerMessages[0]
    ) {
      const recentMsgTime =
        (!offerMessages[0].node.isRead &&
          !messageIsReadUpdates.includes(offerMessages[0].node.id) &&
          offerMessages[0]?.node?.deliveredAt) ||
        '';

      if (!recentMsgTime) {
        return;
      }

      if (!cookies[lastSeenOfferMsgCookie]) {
        setCookie(lastSeenOfferMsgCookie, recentMsgTime);
        setShowDot(true);
      }

      if (new Date(cookies[lastSeenOfferMsgCookie]) < new Date(recentMsgTime)) {
        setCookie(lastSeenOfferMsgCookie, recentMsgTime);
        setShowDot(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerMessages, offerMsgsLoading]);

  return {
    showDot,
    clearDot,
  };
};

export default useShowDot;
